// @flow

import React, { PureComponent } from 'react';
import { withTranslation } from 'next-i18next';
import { connect } from 'react-redux';

import PROJECT_CONSTANTS, { CURRENT_APP_NAME } from '@sharedConstants/mainConstants';
import getInitialRequestParams from '@eventContainersNewsUtils/getInitialRequestParams';

import NewsRegularBlock from 'shared/components/_blocks/news/regular/NewsRegularBlock';
import MetaTags from 'shared/components/meta-tags/MetaTags';

import fetchNews from 'shared/logic/news/fetchNews';

import ScrollHandler from 'shared/libs/ScrollHandler';
import scrollToComponent from 'shared/utils/reactScrollToComponent';
import { getCurrentEventId, getCurrentEventTitle, getCurrentEventImg } from 'event/selectors';
import { handleError } from 'shared/logic/error-handler/action';
import checkIsSmallDevice from 'shared/utils/checkIsSmallDevice';
import SCROLL_TO_COMPONENT_PARAMS from 'shared/constants/scrollToComponent';
import type { PreviewImg } from 'shared/flow-types/img';

import type { NewsItemType, FetchNewsParamsType, NewsSelectorResult } from 'shared/logic/news/types';

type TabsItemType = {
  id: number,
  value: string,
  text: string,
  noItemsText: string
};
type PropsType = {
  currentEventId: string,
  news: Array<NewsItemType>,
  eventImg: PreviewImg,
  eventTitle: string,
  t: Function
};
type StateType = {
  currentTab: TabsItemType,
  news: Array<NewsItemType>,
  isLastPage: boolean,
  isLoad: boolean
};

const TABS: Array<TabsItemType> = [
  { id: 1, value: '', text: 'event.tab_all_news', noItemsText: 'event.no_read' },
  { id: 2, value: 'video', text: 'event.tab_watch', noItemsText: 'event.no_watch' },
  { id: 3, value: 'text', text: 'event.tab_read', noItemsText: 'event.no_read' }
];

export const getServerSidePageProps = async ({ locale, store }) => {
  const {
    list = [],
    meta,
    error
  }: NewsSelectorResult = await fetchNews({
    ...getInitialRequestParams(store.getState().currentEvent.baseInfo.id),
    locale,
    isFormatImg: true
  });

  if (error) {
    store.dispatch(handleError({ error, name: 'in fetching event news', pageName: 'event news' }));
  }

  return {
    props: {
      news: list,
      isLastPage: !meta.nextPage || list.length === 0
    },
    ns: ['news']
  };
};

class EventNewsContainer extends PureComponent<PropsType, StateType> {
  requestParams: FetchNewsParamsType;

  scrollHandler: any;

  scrollToComponentElem: any;

  constructor(props: PropsType) {
    super(props);

    this.state = {
      news: props.news,
      currentTab: TABS[0],
      isLastPage: props.isLastPage,
      isLoad: false
    };
    this.requestParams = getInitialRequestParams(props.currentEventId);
    this.scrollHandler = {};
  }

  componentDidMount() {
    this.scrollHandler = new ScrollHandler(
      this.shouldCalculateScroll,
      () => {
        this.changeRequestParamsAndCallFetch({ isLoadMore: true });
      },
      checkIsSmallDevice() ? 750 : 450
    );

    scrollToComponent(this.scrollToComponentElem, SCROLL_TO_COMPONENT_PARAMS);
  }

  componentDidUpdate(prevProps: PropsType) {
    const { locale, news, isLastPage } = this.props;

    if (prevProps.locale !== locale) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        news,
        currentTab: TABS[0],
        isLastPage,
        isLoad: false
      });
      this.resetRequestParams();
    }
  }

  componentWillUnmount() {
    this.scrollHandler.unlisten();
    this.scrollHandler = {};
  }

  callFetchNews = (isLoadMore: boolean = false): void => {
    const { locale, handleSendError } = this.props;

    this.setState({ isLoad: true }, async () => {
      const { list, meta, error }: NewsSelectorResult = await fetchNews({
        ...this.requestParams,
        locale,
        isFormatImg: true
      });

      if (error) {
        handleSendError(error);
        return;
      }

      this.setState((prevState) => ({
        news: isLoadMore ? [...prevState.news, ...list] : list,
        isLastPage: !meta.nextPage || list.length === 0,
        isLoad: false
      }));
    });
  };

  changeTab = (id: string): void => {
    const currentTab: TabsItemType = TABS.find((item) => item.id === id);

    if (!currentTab) return;

    this.setState({ currentTab });

    this.changeRequestParamsAndCallFetch({ newsFormat: currentTab.value });
  };

  changeRequestParamsAndCallFetch = (params: { newsFormat?: string, isLoadMore?: boolean } = {}): void => {
    if (params.newsFormat) {
      this.requestParams.params.by_format = params.newsFormat;
      this.requestParams.params.page = 1;
    }
    if (params.isLoadMore) {
      this.requestParams.params.page = params.isLoadMore ? this.requestParams.params.page + 1 : 1;
    }

    this.callFetchNews(params.isLoadMore);
  };

  shouldCalculateScroll = (): boolean => {
    const { isLoad, isLastPage } = this.state;
    return !(isLoad || isLastPage);
  };

  resetRequestParams = (): void => {
    const { currentEventId } = this.props;
    this.requestParams = getInitialRequestParams(currentEventId);
  };

  getMetaParams = (): { withoutEvent: boolean, metaTitle: string, metaDesc: string } => {
    const { eventTitle, eventImg, t } = this.props;
    const withoutEvent = !eventTitle && !eventImg.promo;
    const metaTitle = `${t('meta.title')} ${withoutEvent ? '' : ` - ${eventTitle} `}- ${CURRENT_APP_NAME}`;
    const metaDesc = withoutEvent
      ? t('meta.description').replace('%{site}', CURRENT_APP_NAME)
      : `${t('event.meta.description').replace('%{event_name}', eventTitle)} ${CURRENT_APP_NAME}`;

    return { withoutEvent, metaTitle, metaDesc };
  };

  render() {
    const { news, currentTab, isLoad } = this.state;
    const { eventImg, locale } = this.props;
    const { withoutEvent, metaTitle, metaDesc } = this.getMetaParams();

    return (
      <>
        <MetaTags title={metaTitle} description={metaDesc} image={eventImg.promo?.medium} />
        <span
          style={{ width: '0', height: '0' }}
          ref={(ScrollToComponentElem) => {
            this.scrollToComponentElem = ScrollToComponentElem;
          }}
        />
        <NewsRegularBlock
          tabs={TABS}
          newsList={news}
          currentTabId={currentTab.id}
          backgroundImg={eventImg.heading || PROJECT_CONSTANTS.sectionBackground}
          locale={locale}
          isLoad={isLoad}
          styleMod="light"
          withoutEvent={withoutEvent}
          stubTitle={currentTab.noItemsText}
          onTabClick={this.changeTab}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  eventTitle: getCurrentEventTitle(state),
  eventImg: getCurrentEventImg(state),
  currentEventId: getCurrentEventId(state)
});
const mapDispatchToProps = (dispatch) => ({
  handleSendError: (error): void => {
    dispatch(handleError({ error, name: 'in fetching event news', pageName: 'event news' }));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation('news')(EventNewsContainer));
